import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { CircularProgress, Menu, MenuItem } from '@mui/material';

import {
  PaginationDivider,
  PaginationFooter,
  PaginationIconButton,
  PaginationLabel,
  PaginationLeftSection,
  PaginationPill,
  PaginationRightSection,
  PaginationValue,
} from './GeneratikTable.styles';
import { useGeneratikTableContext } from './GeneratikTableContext';
import {
  pageSizeArrowIconSx,
  pageSizeMenuPaperSx,
  paginationInfoPillSx,
  totalLabelSx,
  totalPillSx,
} from './GeneratikTableDefaultDisplayFooter.styles';

interface Props {
  pageSize: number;
  rowCount: number;
  startRow: number;
  endRow: number;
  pageSizeOptions: number[];
  pageTotal?: number | string;
  pageSelected?: number | string;
  spacesTotal?: number;
  productionTotal?: number;
  grandTotal?: number;
  hidePagination?: boolean;
  selectedRowsCount?: number;
  pageSizeAnchor: HTMLElement | null;
  handlePageSizeSelect: (size: number) => void;
  formatTotal: (value: number | string | undefined) => string;
  handlePageSizeClick: (event: React.MouseEvent<HTMLElement>) => void;
  handlePageSizeClose: () => void;
  handlePreviousPage: () => void;
  handleNextPage: () => void;
  canGoPrevious: boolean;
  canGoNext: boolean;
}

const GeneratikTableDefaultDisplayFooter: FC<Props> = ({
  pageSize,
  rowCount,
  startRow,
  endRow,
  pageSizeOptions,
  pageTotal,
  pageSelected,
  spacesTotal,
  productionTotal,
  grandTotal,
  hidePagination,
  pageSizeAnchor,
  handlePageSizeSelect,
  formatTotal,
  handlePageSizeClick,
  handlePageSizeClose,
  handlePreviousPage,
  handleNextPage,
  canGoPrevious,
  canGoNext,
}) => {
  const { t } = useTranslation();
  const ctx = useGeneratikTableContext();

  return (
    <PaginationFooter>
      <PaginationLeftSection>
        {ctx.loading && <CircularProgress data-testid="table-loading-progress" size={25} />}

        {spacesTotal !== undefined ? (
          <PaginationPill data-testid="spaces-total" sx={totalPillSx}>
            <PaginationLabel sx={totalLabelSx}>
              {t('Spaces Total')}: {formatTotal(spacesTotal)}
            </PaginationLabel>
          </PaginationPill>
        ) : (
          pageSelected !== undefined && (
            <PaginationPill data-testid="page-selected" sx={totalPillSx}>
              <PaginationLabel sx={totalLabelSx}>
                {t('Page Selected')}: {formatTotal(pageSelected)}
              </PaginationLabel>
            </PaginationPill>
          )
        )}

        {spacesTotal !== undefined ? (
          <>
            {productionTotal !== undefined && (
              <PaginationPill data-testid="production-total" sx={totalPillSx}>
                <PaginationLabel sx={totalLabelSx}>
                  {t('Services Total')}: {formatTotal(productionTotal)}
                </PaginationLabel>
              </PaginationPill>
            )}

            {pageSelected !== undefined && (
              <PaginationPill data-testid="selection-total" sx={totalPillSx}>
                <PaginationLabel sx={totalLabelSx}>
                  {t('Selection Total')}: {formatTotal(pageSelected)}
                </PaginationLabel>
              </PaginationPill>
            )}

            {grandTotal !== undefined && (
              <PaginationPill data-testid="grand-total" sx={totalPillSx}>
                <PaginationLabel sx={totalLabelSx}>
                  {t('Grand Total')}: {formatTotal(grandTotal)}
                </PaginationLabel>
              </PaginationPill>
            )}
          </>
        ) : (
          pageTotal !== undefined && (
            <PaginationPill data-testid="page-total" sx={totalPillSx}>
              <PaginationLabel sx={totalLabelSx}>
                {t('Page Total')}: {formatTotal(pageTotal)}
              </PaginationLabel>
            </PaginationPill>
          )
        )}
      </PaginationLeftSection>

      <PaginationRightSection>
        {!hidePagination && (
          <>
            <PaginationPill sx={paginationInfoPillSx} data-testid="pagination-info">
              <PaginationValue>
                {startRow}-{endRow}
              </PaginationValue>
              <PaginationLabel>{t('of')}</PaginationLabel>
              <PaginationLabel>{rowCount}</PaginationLabel>

              <PaginationDivider />

              <PaginationIconButton
                onClick={handlePreviousPage}
                disabled={!canGoPrevious}
                aria-label="Go to previous page"
                data-testid="pagination-prev"
              >
                <KeyboardArrowLeftIcon />
              </PaginationIconButton>
              <PaginationIconButton
                onClick={handleNextPage}
                disabled={!canGoNext}
                aria-label="Go to next page"
                data-testid="pagination-next"
              >
                <KeyboardArrowRightIcon />
              </PaginationIconButton>
            </PaginationPill>

            <PaginationPill onClick={handlePageSizeClick} data-testid="page-size-selector">
              <PaginationValue>{pageSize}</PaginationValue>
              <PaginationLabel>{t('per page')}</PaginationLabel>
              <KeyboardArrowDownIcon sx={pageSizeArrowIconSx} />
            </PaginationPill>
            <Menu
              anchorEl={pageSizeAnchor}
              open={Boolean(pageSizeAnchor)}
              onClose={handlePageSizeClose}
              slotProps={{ paper: { sx: pageSizeMenuPaperSx } }}
            >
              {pageSizeOptions.map((size) => (
                <MenuItem
                  key={size}
                  data-testid={`page-size-option-${size}`}
                  onClick={() => handlePageSizeSelect(size)}
                  selected={size === pageSize}
                  dense
                >
                  {size}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </PaginationRightSection>
    </PaginationFooter>
  );
};

export default GeneratikTableDefaultDisplayFooter;
